import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useHistory, Link } from 'react-router-dom';
import OrdersAPI from '../../../api/v3/orders-api-v3';
import ProductsAPI from '../../../api/v3/products-api-v3';
import { OrderCategoriesAPI } from '../../../api/v3/taxonomy-api-v3';
import AsyncLookup from '../../../components/form/AsyncLookup';
import { currentAdminId } from '../../../auth/jwt';
import Select from 'react-select';

const BASE = '/app/sales/bagspa';

// ── Pricing rules ──────────────────────────────────────────────────────────────
const MARKUP_REGULAR = 250_000;
const PRIORITY_FACTOR = 1.5;

const SERVICE_TYPES = [
  { value: 'regular',  label: 'Regular'  },
  { value: 'priority', label: 'Priority' },
];

const VENDORS = [
  { value: 'kaya', label: 'Kaya' },
  { value: 'bnk',  label: 'BNK'  },
];

export const salesNameList = [
  { label: 'Jacqueline - JANE', value: 'Jacqueline' },
  { label: 'Vanya - VALEN', value: 'Vanya' },
  { label: 'Debby - MARTHA', value: 'Debby' },
  { label: 'Cindy - SAM', value: 'Cindy' },
  { label: 'XXX - ZARA', value: 'XXX' },
  { label: 'Amarilis - MARINA', value: 'Amarilis' },
  { label: 'Angel - KANA', value: 'Angel' },
  { label: 'Visha - VINA', value: 'Visha' },
  { label: 'Nisa - RACHEL', value: 'Nisa' },
  { label: 'Jenni - GINA', value: 'Jenni' },
  { label: 'Luthfi - LUVI', value: 'Luthfi' },
  { label: 'Novida - VIVI', value: 'Novida' },
  { label: 'Elsy - EDITA', value: 'Elsy' },
  { label: 'Indra - ANDRA', value: 'Indra' },
  { label: 'Laili - VERA', value: 'Laili' },
  { label: 'Winny - WINONA', value: 'Winny' },
  { label: 'Novi - TANIA', value: 'Novi' },
  { label: 'Celia - ANYA', value: 'Celia' },
  { label: 'Yasyifa - KIKI', value: 'Yasyifa' },
  { label: 'Septi - SARAH', value: 'Septi' },
  { label: 'Mifta - KARLA', value: 'Mifta' },
  { label: 'Not Available', value: 'Not_Available' },
];

const SEARCHABLE_SELECT_STYLES = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 42,
    borderColor: state.isFocused ? '#5b7cfa' : '#dce2ee',
    boxShadow: state.isFocused ? '0 0 0 1px #5b7cfa' : provided.boxShadow,
    '&:hover': {
      borderColor: state.isFocused ? '#5b7cfa' : '#c7d0df',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
  }),
  option: (provided) => ({
    ...provided,
    padding: '10px 14px',
  }),
};

// ── Tooltips ──────────────────────────────────────────────────────────────────
const TIPS = {
  initial:         'Initial Price (admin base): estimasi admin sebelum harga vendor.',
  final:           'Final Price (admin base): harga final dari vendor (Kaya). Sistem otomatis menaikkan sesuai kebijakan.',
  totalInitial:    'Total Initial Price (customer): total harga estimasi ke customer (sudah plus markup).',
  totalFinal:      'Total Final Price (customer): total harga final ke customer (sudah plus markup).',
  advance:         'Advance Payment: pembayaran awal customer pada saat drop-off.',
  initialColHint:  'Input base admin per item; sistem akan menambahkan markup (regular +250k, priority ×1.5 setelah +250k).',
  outstanding:     'Outstanding balance: selisih harga dari Total Final Price - Advance Payment, yakni harga yang harus dibayarkan customer pada saat pengambilan barang atau H+1 (sesuai kesepakatan)',
  finalColHint:    'Input base vendor per item; sistem akan menambahkan markup (regular +250k, priority ×1.5 setelah +250k).',
};

// Clickable tooltip
const InfoTip = ({ title, side = 'bottom', maxWidth = 360 }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const updatePos = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 8;
    const rawTop = side === 'top' ? r.top - gap : r.bottom + gap;
    const rawLeft = r.left;
    const margin = 8;
    const left = Math.max(margin, Math.min(rawLeft, window.innerWidth - (maxWidth + margin)));
    const top  = Math.max(margin, rawTop);
    setPos({ top, left });
  };
  useEffect(() => {
    if (!open) return;
    updatePos();
    const onClickOutside = (e) => {
      const bubble = document.getElementById('__infotip-bubble');
      const inBtn = btnRef.current?.contains(e.target);
      const inBubble = bubble && bubble.contains(e.target);
      if (!inBtn && !inBubble) setOpen(false);
    };
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    const onReflow = () => updatePos();
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, side]);
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="btn btn-link p-0 ml-1"
        onClick={() => setOpen(v => !v)}
        aria-label="Info"
        aria-expanded={open}
        style={{ textDecoration: 'none', lineHeight: 1 }}
      >
        <span
          style={{
            display: 'inline-flex', width: 18, height: 18, borderRadius: '50%',
            border: '1px solid rgba(0,0,0,.35)', color: '#555', fontWeight: 700,
            alignItems: 'center', justifyContent: 'center', fontSize: 12, userSelect: 'none',
          }}
        >
          i
        </span>
      </button>
      {open && createPortal(
        <div
          id="__infotip-bubble"
          role="tooltip"
          style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 2000,
            background: '#fff', border: '1px solid rgba(0,0,0,.12)', boxShadow: '0 4px 16px rgba(0,0,0,.12)',
            borderRadius: 8, padding: '10px 12px', fontSize: 12, lineHeight: 1.4, maxWidth, minWidth: 220,
          }}
        >
          {title}
        </div>,
        document.body
      )}
    </>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const digits = (s) => (s || '').toString().replace(/\D+/g, '');
const toInt = (s) => Number(digits(s)) || 0;
const fmtThousands = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);
const normalizeMoneyInput = (raw) => {
  const n = toInt(raw);
  return { n, text: n ? fmtThousands(n) : '' };
};
function fmtIDR(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return 'Rp 0';
  return 'Rp ' + v.toLocaleString('id-ID');
}
function unwrapItems(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}
function applyMarkup(base, type) {
  const reg = (Number(base) || 0) + MARKUP_REGULAR;
  return Math.round(type === 'priority' ? reg * PRIORITY_FACTOR : reg);
}
function baseFromCharged(charged, type) {
  const val = Number(charged) || 0;
  if (type === 'priority') return Math.max(0, Math.round(val / PRIORITY_FACTOR - MARKUP_REGULAR));
  return Math.max(0, Math.round(val - MARKUP_REGULAR));
}
const norm = (s) => (s ?? '').toString().trim().toLowerCase();
const normalizeVendor = (v) => (v === 'bnk' || v === 'kaya' ? v : 'kaya');
const normalizeOrderType = (v) => (v === 'priority' ? 'priority' : 'regular');

export default function OrderFormPage({ isEdit = false }) {
  const { id } = useParams();
  const history = useHistory();

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [orderCategoryId, setOrderCategoryId] = useState('');
  const [status, setStatus] = useState('draft');
  const [vendor, setVendor] = useState('kaya');
  const [assistBy, setAssistBy] = useState('');

  const [deliveryCostText, setDeliveryCostText] = useState('');
  const [estimatedCompletion, setEstimatedCompletion] = useState('');
  const [notes, setNotes] = useState('');

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);

  // add-row state
  const [newProduct, setNewProduct] = useState(null);
  const [newCategory, setNewCategory] = useState(null);
  const [newType, setNewType] = useState(normalizeOrderType('regular'));

  // Load categories
  useEffect(() => {
    OrderCategoriesAPI.list().then((payload) => setCategories(unwrapItems(payload)));
  }, []);

  // Edit mode: load order and reconstruct admin bases
  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        const json = await OrdersAPI.show(id);
        const o = json?.item || json?.data || json;

        setOrder(o);
        setCustomer(o.customer || null);
        setOrderCategoryId(o.order_category_id || '');
        setStatus(typeof o.status === 'string' ? o.status : String(o.status || 'draft'));
        setVendor(normalizeVendor(o.vendor));
        
        const assistByValue =
          typeof o.assist_by === 'string'
            ? o.assist_by
            : (o.assist_by?.value || o.assist_by?.name || '');

        setAssistBy(assistByValue);

        setDeliveryCostText(o.delivery_cost ? fmtThousands(o.delivery_cost) : '');
        setEstimatedCompletion(o.estimated_completion_date || '');
        setNotes(o.additional_notes ?? '');

        const prefilled = (o.order_items || []).map((it) => {
          const ot = it.order_type === 'priority' ? 'priority' : 'regular';

          // reconstruct bases from fixed if bases missing
          const initBase =
            Number(it.initial_vendor_price) ||
            baseFromCharged(Number(it.initial_fixed_price ?? it.initial_price) || 0, ot);

          const finalBase =
            Number(it.final_vendor_price) ||
            (it.final_fixed_price ? baseFromCharged(Number(it.final_fixed_price), ot) : 0);

          return {
            id: it.id,
            product_id: it.product_id || it.id,
            product: {
              id: it.product_id || it.id,
              label: `${it.sku || ''} (${it.brand_name || '—'})`,
              category_name: it.category_name,
              size_code: it.size_code,
              material_name: it.material_name,
            },
            order_category_id: it.order_category_id,
            order_category: it.order_category || null,
            order_type: ot,
            initial_input_text: initBase ? fmtThousands(initBase) : '',
            final_input_text: finalBase ? fmtThousands(finalBase) : '', // EDIT ONLY
            _locked_type: true,
          };
        });
        setItems(prefilled);
      } catch (e) {
        setError(e.message || 'Failed to load order.');
      }
    })();
  }, [isEdit, id]);

  // Fetchers
  const fetchCustomers = useCallback(async (q) => {
    const API_BASE = process.env.REACT_APP_API_BASE;
    const url = new URL(`${API_BASE}/api/v3/customers`);
    if (q) url.searchParams.set('q', q);
    url.searchParams.set('per', '20');
    const res = await fetch(url.toString(), { credentials: 'include', headers: { Accept: 'application/json' }});
    const json = await res.json();
    return unwrapItems(json).map((c) => ({
      id: c.id, label: `${c.name || '-'} · ${c.email || ''}`, name: c.name, email: c.email,
    }));
  }, []);

  const fetchProducts = useCallback(async (q) => {
    if (!customer?.id) return [];
    const res = await ProductsAPI.list({ q, customer_id: customer.id, per: 20 });
    const arr = unwrapItems(res);
    return arr.map((p) => ({
      id: p.id,
      label: `${p.sku} (${p.brand_name || '—'}) (${p.category_name || '—'}) (${p.size_code || '—'}) (${p.material_name || '—'})`,
      sku: p.sku,
      brand_name: p.brand_name,
      category_name: p.category_name,
      size_code: p.size_code,
      material_name: p.material_name,
    }));
  }, [customer?.id]);

  // charged price helpers
  const chargedInitial = (it) => applyMarkup(toInt(it.initial_input_text), normalizeOrderType(it.order_type));
  const chargedFinal   = (it) => {
    const fb = toInt(it.final_input_text);
    if (!fb) return null;
    return applyMarkup(fb, normalizeOrderType(it.order_type));
  };

  // Totals (initial always available; final only if any final exists)
  const totals = useMemo(() => {
    const init = items.reduce((sum, it) => sum + applyMarkup(toInt(it.initial_input_text), normalizeOrderType(it.order_type)), 0);
    const finals = items.map(chargedFinal).filter((v) => typeof v === 'number' && v > 0);
    const fin = finals.length ? finals.reduce((a,b)=>a+b, 0) : 0;
    const adv = init; // default policy: DP suggested = initial total
    return { init, fin, adv };
  }, [items]);

  function onChangeInitial(idx, raw) {
    const { text } = normalizeMoneyInput(raw);
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, initial_input_text: text } : it));
  }
  function onChangeFinal(idx, raw) {
    const { text } = normalizeMoneyInput(raw);
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, final_input_text: text } : it));
  }

  // Filter order categories by selected product traits
  const productTraits = useMemo(() => {
    if (!newProduct) return null;
    return {
      category: newProduct.category_name,
      size: newProduct.size_code,
      material: newProduct.material_name,
    };
  }, [newProduct]);

  const filteredCategories = useMemo(() => {
    if (!categories?.length) return [];
    if (!productTraits) return categories;
    return categories.filter((c) => {
      const okCat = productTraits.category ? norm(c.category) === norm(productTraits.category) : true;
      const okSize = productTraits.size ? norm(c.size) === norm(productTraits.size) : true;
      const okMat  = productTraits.material ? norm(c.material) === norm(productTraits.material) : true;
      return okCat && okSize;
    });
  }, [categories, productTraits]);

  useEffect(() => {
    setNewCategory(null);
    if (filteredCategories.length === 1) setNewCategory(filteredCategories[0]);
  }, [newProduct, filteredCategories.length]); // eslint-disable-line

  function addItem() {
    if (!customer?.id) return alert('Select a customer first.');
    if (!newProduct || !newCategory) return alert('Select product and category.');

    const newItem = {
      product_id: newProduct.id,
      product: newProduct,
      order_category_id: newCategory.id,
      order_category: newCategory,
      order_type: normalizeOrderType(newType),
      initial_input_text: '',
      final_input_text: '', // still present in state; not shown on CREATE UI
      _locked_type: true,
    };
    setItems(prev => [...prev, newItem]);

    setNewProduct(null);
    setNewCategory(null);
    setNewType(normalizeOrderType('regular'));
  }

  function removeItem(idx) {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (!customer?.id) throw new Error('Please select a customer.');
      if (!assistBy) throw new Error('Please select Assist By.');
      if (items.length === 0) throw new Error('Please add at least one item.');

      const order_items_attributes = items.map((it) => {
        const initBase = toInt(it.initial_input_text); // admin base (initial)
        const finBase  = toInt(it.final_input_text);   // admin base (final)

        const safeOrderType = normalizeOrderType(it.order_type);
        const initFixed = initBase > 0 ? applyMarkup(initBase, safeOrderType) : 0; // charged initial
        const finFixed  = finBase  > 0 ? applyMarkup(finBase,  safeOrderType) : 0; // charged final

        const common = {
          quantity: 1,
          order_type: safeOrderType,

          // always send both base & charged values
          initial_vendor_price: initBase,
          initial_fixed_price:  initFixed,
          final_vendor_price:   finBase,
          final_fixed_price:    finFixed,

          // legacy mirrors if your BE still references them anywhere
          initial_price: initFixed,
          final_price:   finFixed,

          _destroy: false,
        };

        // if editing existing line
        if (it.id) return { id: it.id, order_category_id: it.order_category_id, ...common };

        // if creating new line
        return {
          product_id: it.product_id || it.product?.id,
          order_category_id: it.order_category_id,
          ...common,
        };
      });

      const headerInit = order_items_attributes.reduce((s, oi) => s + (oi.initial_fixed_price || 0), 0);
      const headerFin  = order_items_attributes.reduce((s, oi) => s + (oi.final_fixed_price   || 0), 0);

      const payload = {
        customer_id: customer.id,
        order_category_id: orderCategoryId || null,
        status: isEdit ? status : 'draft',
        vendor: normalizeVendor(vendor),
        assist_by: assistBy || '',
        initial_fixed_price: headerInit,
        final_fixed_price:   headerFin,                 // <- sum of per-item final_fixed_price
        advance_payment_price: headerInit,              // your “DP = initial total” policy
        delivery_cost: toInt(deliveryCostText),
        estimated_completion_date: estimatedCompletion || null,
        additional_notes: notes || '',
        order_items_attributes,
      };

      if (!isEdit) {
        const saId = currentAdminId();
        if (saId) payload.sales_assistant_id = saId;
        const created = await OrdersAPI.create(payload);
        const newId = created?.id || created?.item?.id;
        history.push(`${BASE}/orders/${newId}`);
      } else {
        await OrdersAPI.update(id, payload);
        history.push(`${BASE}/orders/${id}`);
      }
    } catch (e) {
      setError(e.message || 'Failed to save order.');
    } finally {
      setSaving(false);
    }
  }

  const selectedAssistByOption = salesNameList.find(
    (sales) => sales.value === assistBy
  ) || (assistBy ? { label: assistBy, value: assistBy } : null);

  const selectedAssistByLabel = selectedAssistByOption?.label || assistBy || '—';

  // ---------- render ----------
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">{isEdit ? 'Edit' : 'New'} Order</h2>
        <Link to={`${BASE}/orders`} className="btn btn-light">&larr; Back</Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="alert alert-secondary py-2">
        <div>
          <b>Sales Assistant:</b>{' '}
          <span className="ml-1">
            {isEdit
              ? (
                order?.sales_assistant?.first_name ||
                order?.sales_assistant?.name ||
                order?.sales_assistant_name ||
                '—'
              )
              : 'Auto-assigned on create'}
          </span>
        </div>

        <div>
          <b>Assist By:</b>{' '}
          <span className="ml-1">{selectedAssistByLabel}</span>   
        </div>
      </div>

      <form onSubmit={onSubmit}>
        {/* Customer */}
        <div className="form-group">
          <label>Customer</label>
          <AsyncLookup
            value={customer}
            onChange={(val) => {
              setCustomer(val);
              setItems([]);
              setNewProduct(null);
              setNewCategory(null);
            }}
            fetchFn={fetchCustomers}
            placeholder="Search customer…"
          />
        </div>

        {/* Status */}
        <div className="form-group">
          <label>Status</label>
          {isEdit ? (
            <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="draft">Pending</option>
              <option value="confirmed">confirmed</option>
              <option value="processing">processing</option>
              <option value="completed">completed</option>
              <option value="cancelled">cancelled</option>
              <option value="accepted">accepted</option>
            </select>
          ) : (
            <input className="form-control" value="Pending" disabled />
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Vendor</label>
          <select
            className="form-control"
            value={vendor || 'kaya'}
            onChange={(e) => setVendor(normalizeVendor(e.target.value))}
          >
            {VENDORS.map(v => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
          <small className="form-text text-muted">Default: Kaya</small>
        </div>

        <div className="form-group">
          <label className="form-label">
            Assist By <span className="text-danger">*</span>
          </label>
          <Select
            classNamePrefix="assist-by-select"
            styles={SEARCHABLE_SELECT_STYLES}
            value={selectedAssistByOption}
            options={salesNameList}
            onChange={(selected) => setAssistBy(selected?.value || '')}
            isClearable
            isSearchable
            placeholder="Search sales assist..."
            noOptionsMessage={() => 'No sales data available'}
          />
          <small className="form-text text-muted">
            Mandatory. Cari dan pilih sales yang membantu proses order ini.
          </small>
        </div>

        {/* Delivery + Estimated completion */}
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Delivery Cost</label>
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              value={deliveryCostText}
              onChange={(e) => setDeliveryCostText(normalizeMoneyInput(e.target.value).text)}
              placeholder="100.000"
            />
          </div>
          <div className="form-group col-md-6">
            <label>Estimated Completion</label>
            <input
              type="date"
              className="form-control"
              value={estimatedCompletion}
              onChange={(e) => setEstimatedCompletion(e.target.value)}
            />
          </div>
        </div>

        {/* Totals */}
        <div className="form-row">
          <div className="form-group col-md-4">
            <label>
              Total Initial Price (customer) <InfoTip title={TIPS.totalInitial} />
            </label>
            <input type="text" className="form-control" value={fmtIDR(totals.init)} readOnly />
          </div>
          <div className="form-group col-md-4">
            <label>
              Total Final Price (customer) <InfoTip title={TIPS.totalFinal} />
            </label>
            <input
              type="text"
              className="form-control"
              value={fmtIDR(totals.fin || 0)}
              readOnly
            />
          </div>
          <div className="form-group col-md-4">
            <label>
              Advance Payment <InfoTip title={TIPS.advance} />
            </label>
            <input type="text" className="form-control" value={fmtIDR(totals.adv)} readOnly />
          </div>
        </div>

        {/* Notes */}
        <div className="form-group">
          <label>Notes</label>
          <textarea
            className="form-control"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes…"
          />
        </div>

        {/* Items */}
        <h5 className="mt-4">Order Items</h5>
        {items.length ? (
          <div className="table-responsive mb-2">
            <table className="table table-sm table-hover align-middle">
              <thead className="thead-light">
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Product</th>
                  <th>Category</th>
                  <th style={{ width: 140 }}>Layanan</th>
                  <th style={{ width: 220 }}>
                    Initial / unit (admin base)
                    <InfoTip title={TIPS.initialColHint} />
                  </th>
                  {isEdit && (
                    <th style={{ width: 220 }}>
                      Final / unit (admin base)
                      <InfoTip title={TIPS.finalColHint} />
                    </th>
                  )}
                  <th style={{ width: 160 }}>Subtotal (charged)</th>
                  <th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td className="text-monospace">{it.product?.label || it.product_id}</td>
                    <td>{it.order_category?.name || it.order_category_id || '—'}</td>
                    <td>
                      <select className="form-control form-control-sm" value={it.order_type} disabled readOnly>
                        {SERVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control form-control-sm"
                        value={it.initial_input_text ?? ''}
                        onChange={(e) => onChangeInitial(idx, e.target.value)}
                        placeholder="1.000.000"
                        title={TIPS.initial}
                      />
                      <small className="text-muted d-block">
                        Charged (initial): {fmtIDR(chargedInitial(it))} ({it.order_type === 'priority' ? '1.5 × (base + 250k)' : 'base + 250k'})
                      </small>
                    </td>
                    {isEdit && (
                      <td>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control form-control-sm"
                          value={it.final_input_text ?? ''}
                          onChange={(e) => onChangeFinal(idx, e.target.value)}
                          placeholder="1.200.000"
                          title={TIPS.final}
                        />
                        <small className="text-muted d-block">
                          Charged (final): {fmtIDR(chargedFinal(it) || 0)} ({it.order_type === 'priority' ? '1.5 × (base + 250k)' : 'base + 250k'})
                        </small>
                      </td>
                    )}
                    <td className="text-nowrap">
                      {fmtIDR(isEdit && chargedFinal(it) ? chargedFinal(it) : chargedInitial(it))}
                    </td>
                    <td>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeItem(idx)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={isEdit ? 6 : 5} className="text-right">Subtotal</th>
                  <th className="text-nowrap">
                    {fmtIDR(isEdit && totals.fin > 0 ? totals.fin : totals.init)}
                  </th>
                  <th />
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-muted">No items yet</div>
        )}

        {/* Add item row */}
        <div className="d-flex align-items-center mt-2 flex-wrap" style={{ gap: 24 }}>
          <AsyncLookup
            value={newProduct}
            onChange={(val) => { setNewProduct(val); setNewCategory(null); }}
            fetchFn={fetchProducts}
            placeholder={customer?.id ? "Search this customer's products…" : 'Select customer first'}
            disabled={!customer?.id}
          />

          <div className="d-flex align-items-center" style={{ minWidth: 320 }}>
            <select
              className="form-control"
              value={newCategory?.id || ''}
              onChange={(e) => {
                const idNum = parseInt(e.target.value, 10);
                const cat = filteredCategories.find((c) => c.id === idNum);
                setNewCategory(cat || null);
              }}
              disabled={!customer?.id || !newProduct || filteredCategories.length === 0}
            >
              <option value="">
                {newProduct
                  ? (filteredCategories.length ? 'Select Category' : 'No matching category')
                  : 'Select product first'}
              </option>
              {filteredCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} — {c.category} - {c.size} - {c.material}
                </option>
              ))}
            </select>
          </div>

          <select
            className="form-control"
            value={newType}
            onChange={(e) => setNewType(normalizeOrderType(e.target.value))}
            disabled={!customer?.id}
          >
            {SERVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>

          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={addItem}
            disabled={!customer?.id || !newProduct || !newCategory}
          >
            Add
          </button>
        </div>

        {newProduct && (
          <div className="small text-muted mt-1">
            Filtering by: <b>{newProduct.category_name || '—'}</b> / <b>{newProduct.size_code || '—'}</b> / <b>{newProduct.material_name || '—'}</b>
          </div>
        )}

        <hr />
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : (isEdit ? 'Save Order' : 'Create Order')}
        </button>
      </form>
    </div>
  );
}
